import type {
  PaginatedResult,
  PublicCategorySummary,
  PublicProductDetail,
  PublicProductSummary,
} from '@emazy/shared-types';
import { z } from 'zod';

import { ApiClient } from '@/lib/api';

import {
  catalogSlugSchema,
  categoryListInputSchema,
  envelopeSchema,
  pageSchema,
  productListInputSchema,
  publicCategorySchema,
  publicProductDetailSchema,
  publicProductSummarySchema,
} from './catalogApiSchemas';
import type {
  FeaturedProductListInput,
  PublicCategoryListInput,
  PublicCatalogService,
  PublicProductListInput,
} from './types';
import { CatalogError } from './types';

const backendSort = { featured: 'merchandising', newest: 'recent' } as const;

export class PublicCatalogApi implements PublicCatalogService {
  constructor(private readonly client: ApiClient) {}

  private async get<T>(path: string, schema: z.ZodType<T>, signal?: AbortSignal): Promise<T> {
    let response: Response;
    try {
      response = await this.client.request(path, {
        auth: 'public',
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new CatalogError('network-error', 'The catalog could not be reached.', undefined, {
        cause: error,
      });
    }
    if (!response.ok) {
      const notFound = response.status === 404;
      throw new CatalogError(
        notFound
          ? path.includes('/categories/')
            ? 'category-not-found'
            : 'product-not-found'
          : response.status >= 500
            ? 'catalog-unavailable'
            : 'unknown',
        'The catalog request was not successful.',
        response.status,
      );
    }
    try {
      return envelopeSchema(schema).parse(await response.json()).data;
    } catch (error) {
      throw new CatalogError('invalid-response', 'The catalog returned invalid data.', undefined, {
        cause: error,
      });
    }
  }

  async listCategories(input: PublicCategoryListInput = {}, signal?: AbortSignal) {
    const parsed = parseInput(categoryListInputSchema, input);
    return this.get<PaginatedResult<PublicCategorySummary>>(
      `/v1/catalog/categories${query(parsed)}`,
      pageSchema(publicCategorySchema),
      signal,
    );
  }

  async getCategory(slug: string, signal?: AbortSignal) {
    const parsed = parseInput(catalogSlugSchema, slug);
    return this.get<PublicCategorySummary>(
      `/v1/catalog/categories/${encodeURIComponent(parsed)}`,
      publicCategorySchema,
      signal,
    );
  }

  async listProducts(input: PublicProductListInput = {}, signal?: AbortSignal) {
    const parsed = parseInput(productListInputSchema, input);
    const request = { ...parsed, sort: parsed.sort ? backendSort[parsed.sort] : undefined };
    return this.get<PaginatedResult<PublicProductSummary>>(
      `/v1/catalog/products${query(request)}`,
      pageSchema(publicProductSummarySchema),
      signal,
    );
  }

  async listCategoryProducts(
    categorySlug: string,
    input: Omit<PublicProductListInput, 'category'> = {},
    signal?: AbortSignal,
  ) {
    return this.listProducts({ ...input, category: categorySlug }, signal);
  }

  async getProduct(slug: string, signal?: AbortSignal) {
    const parsed = parseInput(catalogSlugSchema, slug);
    return this.get<PublicProductDetail>(
      `/v1/catalog/products/${encodeURIComponent(parsed)}`,
      publicProductDetailSchema,
      signal,
    );
  }

  async listFeaturedProducts(input: FeaturedProductListInput = {}, signal?: AbortSignal) {
    const parsed = parseInput(categoryListInputSchema, input);
    return this.get<PaginatedResult<PublicProductSummary>>(
      `/v1/catalog/featured-products${query(parsed)}`,
      pageSchema(publicProductSummarySchema),
      signal,
    );
  }
}

function parseInput<T>(schema: z.ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input);
  if (!result.success)
    throw new CatalogError('invalid-filter', 'Catalog request filters are invalid.', 400, {
      cause: result.error,
    });
  return result.data;
}

function query(values: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const result = search.toString();
  return result ? `?${result}` : '';
}
